import { useQuery } from '@apollo/client';
import { Badge } from '@synoptic/ui-kit/badge/badge.js';
import { Checkbox } from '@synoptic/ui-kit/checkbox/checkbox.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@synoptic/ui-kit/collapsible/collapsible.js';
import { MinusIcon } from '@synoptic/ui-kit/icons/react/minus.js';
import { NavArrowDownIcon } from '@synoptic/ui-kit/icons/react/nav-arrow-down.js';
import { PlusIcon } from '@synoptic/ui-kit/icons/react/plus.js';
import { Input } from '@synoptic/ui-kit/input/input.js';
import { LinkButton } from '@synoptic/ui-kit/link-button/link-button.js';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@synoptic/ui-kit/popover/popover.js';
import { Spinner } from '@synoptic/ui-kit/spinner/spinner.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@synoptic/ui-kit/tooltip/tooltip.js';
import { useEffect, useState } from 'react';
import { gql } from '@/__generated__';
import { Stream } from '@/__generated__/schema-types';
import { TargetsModal } from '@/components/streams/custom-stream/targets-modal';
import { useMe } from '@/routes/_app/use-me';
import {
  isSubscribedCustomStream,
  SourceCustomTargetsMap,
} from './source-custom-targets';
import { expandableSwitchGroupLabel, group } from '../list-form.css';
import {
  chevron,
  chipBadge,
  chipBadgeText,
  countBadge,
  emptySearchResult,
  headerSummary,
  noShrinkChipBadge,
  popoverFooter,
  popoverHeader,
  popoverSearch,
  rowRight,
  streamName,
  streamRow,
  streamsList,
  targetItem,
  targetItemText,
  targetsList,
  targetsPopover,
  tooltipTargetChipText,
  tooltipTargets,
} from './custom-targets-section.css';

export const CustomTargetsSection = ({
  sources,
  value,
  onChange,
}: {
  sources: Stream[];
  value: SourceCustomTargetsMap;
  onChange: (value: SourceCustomTargetsMap) => void;
}) => {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const customStreams = sources.filter((stream) =>
    isSubscribedCustomStream(stream, me.id),
  );

  const setStreamTargets = (streamId: string, targets?: string[]) => {
    if (targets === undefined) {
      const { [streamId]: _removed, ...rest } = value;
      onChange(rest);
    } else {
      onChange({ ...value, [streamId]: targets });
    }
  };

  if (customStreams.length === 0) {
    return null;
  }

  const summary = `${customStreams.length} ${
    customStreams.length === 1 ? 'stream' : 'streams'
  }`;

  return (
    <div className={group}>
      {/* Fetches targets and prunes stale selections even while the section is collapsed */}
      {customStreams.map((stream) => (
        <StreamTargetsSync
          key={stream.id}
          streamId={stream.id}
          explicitTargets={value[stream.id]}
          onExplicitTargetsChange={(targets) =>
            setStreamTargets(stream.id, targets)
          }
        />
      ))}
      <Collapsible
        open={open}
        onOpenChange={setOpen}
        data-testid="custom-targets"
      >
        <CollapsibleTrigger asChild>
          <div className={expandableSwitchGroupLabel}>
            Custom targets
            {!open && <span className={headerSummary}>{summary}</span>}
            {open ? (
              <MinusIcon width={20} height={20} />
            ) : (
              <PlusIcon width={20} height={20} />
            )}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className={streamsList}>
            {customStreams.map((stream) => (
              <StreamTargetsRow
                key={stream.id}
                stream={stream}
                explicitTargets={value[stream.id]}
                onExplicitTargetsChange={(targets) =>
                  setStreamTargets(stream.id, targets)
                }
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const StreamTargetsSync = ({
  streamId,
  explicitTargets,
  onExplicitTargetsChange,
}: {
  streamId: string;
  explicitTargets?: string[];
  onExplicitTargetsChange: (targets: string[] | undefined) => void;
}) => {
  const { data } = useQuery(ListFormStreamTargetsQuery, {
    variables: { id: streamId },
  });
  const customTargets = data?.stream?.customTargets;

  // Drop selections that are no longer part of the subscription
  useEffect(() => {
    if (!customTargets || explicitTargets === undefined) {
      return;
    }
    const knownTargets = new Set(customTargets);
    if (explicitTargets.some((target) => !knownTargets.has(target))) {
      const pruned = explicitTargets.filter((target) =>
        knownTargets.has(target),
      );
      onExplicitTargetsChange(
        pruned.length === customTargets.length ? undefined : pruned,
      );
    }
  }, [customTargets, explicitTargets, onExplicitTargetsChange]);

  return null;
};

const ListFormStreamTargetsQuery = gql(`
  query ListFormStreamTargetsQuery($id: ID!) {
    stream(id: $id) {
      id
      customTargets
      customTargetsData {
        ...CustomTargetsDataFragment
      }
      subscription {
        active
        cancelAt
        price
        customTargets
        interval
        intervalCount
      }
    }
  }
`);

const PREVIEW_TARGETS_COUNT = 2;
const SEARCH_TARGETS_THRESHOLD = 4;

const StreamTargetsRow = ({
  stream,
  explicitTargets,
  onExplicitTargetsChange,
}: {
  stream: Stream;
  /** Explicit subset picked by the user; undefined = all targets (usesAllTargets) */
  explicitTargets?: string[];
  onExplicitTargetsChange: (targets: string[] | undefined) => void;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isManageTargetsOpen, setIsManageTargetsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data, loading } = useQuery(ListFormStreamTargetsQuery, {
    variables: { id: stream.id },
  });
  const streamDetails = data?.stream;
  const allTargets = streamDetails?.customTargets ?? [];

  const selectedTargets = explicitTargets ?? allTargets;

  const updateSelection = (next: string[]) => {
    // Selecting every target collapses back to the "all targets" default
    onExplicitTargetsChange(
      allTargets.length > 0 && next.length === allTargets.length
        ? undefined
        : next,
    );
  };

  const visibleTargets = search
    ? allTargets.filter((target) =>
        target.toLowerCase().includes(search.toLowerCase()),
      )
    : allTargets;

  const previewTargets = selectedTargets.slice(0, PREVIEW_TARGETS_COUNT);
  const hiddenSelectedTargets = selectedTargets.slice(PREVIEW_TARGETS_COUNT);

  const allSelected =
    allTargets.length > 0 && selectedTargets.length === allTargets.length;

  const toggleTarget = (target: string) => {
    updateSelection(
      selectedTargets.includes(target)
        ? selectedTargets.filter((t) => t !== target)
        : [...selectedTargets, target],
    );
  };

  return (
    <>
      <Popover
        // modal popover installs its own scroll lock so the list scrolls inside the modal
        modal
        open={isPopoverOpen}
        onOpenChange={(open) => {
          setIsPopoverOpen(open);
          if (!open) {
            setSearch('');
          }
        }}
      >
        <PopoverTrigger asChild>
          <button type="button" className={streamRow}>
            <span className={streamName}>{stream.name}</span>
            <Badge color="neutral" className={countBadge}>
              {loading
                ? '…'
                : selectedTargets.length > 0
                  ? `${selectedTargets.length}/${allTargets.length}`
                  : 'All'}
            </Badge>
            <span className={rowRight}>
              {loading ? null : selectedTargets.length === 0 ? (
                <Badge color="neutral" className={noShrinkChipBadge}>
                  All targets
                </Badge>
              ) : (
                <>
                  {previewTargets.map((target) => (
                    <Badge key={target} color="neutral" className={chipBadge}>
                      <span className={chipBadgeText}>{target}</span>
                    </Badge>
                  ))}
                  {hiddenSelectedTargets.length > 0 ? (
                    <Tooltip delayDuration={100}>
                      <TooltipTrigger asChild>
                        <Badge color="neutral" className={noShrinkChipBadge}>
                          +{hiddenSelectedTargets.length}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent
                        render={() => (
                          <span className={tooltipTargets}>
                            {hiddenSelectedTargets.map((target) => (
                              <Badge
                                key={target}
                                color="neutral"
                                className={chipBadge}
                              >
                                <span className={tooltipTargetChipText}>
                                  {target}
                                </span>
                              </Badge>
                            ))}
                          </span>
                        )}
                      />
                    </Tooltip>
                  ) : null}
                </>
              )}
              <NavArrowDownIcon width={16} height={16} className={chevron} />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className={targetsPopover}
          side="bottom"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {allTargets.length > SEARCH_TARGETS_THRESHOLD ? (
            <div className={popoverSearch}>
              <Input
                placeholder="Search targets..."
                aria-label={`Search targets in ${stream.name}`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          ) : null}
          <div className={popoverHeader}>
            <span>Targets</span>
            <Checkbox
              checked={
                allSelected
                  ? true
                  : selectedTargets.length > 0
                    ? 'indeterminate'
                    : false
              }
              disabled={allTargets.length === 0}
              aria-label={`Toggle all targets of ${stream.name}`}
              onCheckedChange={() =>
                updateSelection(allSelected ? [] : allTargets)
              }
            />
          </div>
          <div className={targetsList}>
            {loading ? (
              <div className={emptySearchResult}>
                <Spinner size={16} />
              </div>
            ) : visibleTargets.length > 0 ? (
              visibleTargets.map((target) => (
                <label key={target} className={targetItem}>
                  <Checkbox
                    checked={selectedTargets.includes(target)}
                    aria-label={`Toggle target ${target}`}
                    onCheckedChange={() => toggleTarget(target)}
                  />
                  <span className={targetItemText}>{target}</span>
                </label>
              ))
            ) : (
              <div className={emptySearchResult}>
                {search ? 'No targets found' : 'No targets in this stream yet'}
              </div>
            )}
          </div>
          <div className={popoverFooter}>
            <span>
              {selectedTargets.length > 0
                ? `${selectedTargets.length} of ${allTargets.length} selected`
                : 'All targets included'}
            </span>
            <LinkButton
              size="small"
              onClick={() => {
                setIsPopoverOpen(false);
                setIsManageTargetsOpen(true);
              }}
            >
              Manage targets
            </LinkButton>
          </div>
        </PopoverContent>
      </Popover>
      {streamDetails ? (
        <TargetsModal
          streamId={stream.id}
          open={isManageTargetsOpen}
          onOpenChange={setIsManageTargetsOpen}
          maxTargets={streamDetails.subscription?.customTargets ?? 0}
          initialTargets={streamDetails.customTargets ?? []}
          instruction={streamDetails.customTargetsData?.targetTypeInstruction}
          targetValidationRegex={
            streamDetails.customTargetsData?.targetValidationRegex
          }
          customTargetsData={streamDetails.customTargetsData}
          subscription={streamDetails.subscription}
        />
      ) : null}
    </>
  );
};
